import React, { useMemo, useState } from 'react';
import { Users, User, Building, Phone, Mail, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { formatValue } from '../../utils/formatters/valueUtils';
import { formatDateBr } from '../../utils/formatters/dateUtils';
import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';

/**
 * Componente para visualização de dados de entidades (PF e PJ)
 * relacionadas a uma propriedade, seguindo padrões visuais e
 * utilizando utilitários compartilhados.
 */
const EntidadesTab = ({ propertyData }) => {
  const [expandedPjCards, setExpandedPjCards] = useState({});

  const parsed = useMemo(() => {
    const props = propertyData?.properties || {};
    const entidadesKey = Object.keys(props).find((k) => k.toLowerCase() === 'entidades');
    if (!entidadesKey) return { pf: [], pj: [] };

    const source = props[entidadesKey];
    const list = Array.isArray(source) ? source : source ? [source] : [];

    const pf = [];
    const pj = [];

    const normalizePf = (raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const cadastro = raw.cadastro || raw;
      return {
        nome: cadastro.nome || cadastro.nomeCompleto || '--',
        ni: cadastro.ni || cadastro.documento || '--',
        nascimento: cadastro.nascimento || cadastro.dataNascimento || '--',
        data_inscricao:
          cadastro.data_inscricao || cadastro.dataInscricao || cadastro.datainscricao || '--',
        situacao_descricao:
          cadastro.situacao_descricao ||
          cadastro.situacao?.descricao ||
          (Array.isArray(cadastro.situacao)
            ? cadastro.situacao
                .map((s) => s?.descricao)
                .filter(Boolean)
                .join(', ')
            : '--'),
      };
    };

    const normalizePj = (raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const cadastro = raw.cadastro || raw;
      return {
        ni: cadastro.ni || '--',
        tipo_estabelecimento: cadastro.tipo_estabelecimento || cadastro.tipoEstabelecimento || '--',
        nome_empresarial: cadastro.nome_empresarial || cadastro.nomeEmpresarial || '--',
        nome_fantasia: cadastro.nome_fantasia || cadastro.nomeFantasia || '--',
        data_abertura: cadastro.data_abertura || cadastro.dataAbertura || '--',
        natureza_juridica:
          cadastro.natureza_juridica?.descricao ||
          (Array.isArray(cadastro.naturezaJuridica)
            ? cadastro.naturezaJuridica
                .map((n) => n?.descricao)
                .filter(Boolean)
                .join(', ')
            : cadastro.naturezaJuridica?.descricao) ||
          '--',
        endereco: cadastro.endereco || null,
        telefones: Array.isArray(cadastro.telefones) ? cadastro.telefones : [],
        correio_eletronico: cadastro.correio_eletronico || cadastro.correioEletronico || '--',
      };
    };

    for (const item of list) {
      if (item?.pf || item?.pj) {
        const pfs = Array.isArray(item.pf) ? item.pf : [];
        const pjs = Array.isArray(item.pj) ? item.pj : [];
        pf.push(...pfs.map(normalizePf).filter(Boolean));
        pj.push(...pjs.map(normalizePj).filter(Boolean));
        continue;
      }

      // Fallback legado: array único misto
      const rawTipo = String(item?.tipo || item?.tipo_documento || '').toUpperCase();
      const doc = String(item?.documento || item?.cadastro?.ni || '').replace(/\D/g, '');
      const resolvedTipo = rawTipo || (doc.length === 11 ? 'PF' : doc.length === 14 ? 'PJ' : '');
      if (resolvedTipo === 'PF') {
        const normalizedPf = normalizePf(item);
        if (normalizedPf) pf.push(normalizedPf);
      } else {
        const normalizedPj = normalizePj(item);
        if (normalizedPj) pj.push(normalizedPj);
      }
    }

    return { pf, pj };
  }, [propertyData]);

  if (parsed.pf.length === 0 && parsed.pj.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <div className="flex items-center text-gray-500">
          <Users className="mr-2" size={18} />
          <p>Nenhuma entidade encontrada para esta propriedade.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center text-blue-700">
          <Users size={20} className="mr-2" />
          <h3 className="text-lg font-medium">Entidades</h3>
        </div>
        <div className="text-xs text-gray-500">
          PF: {parsed.pf.length} | PJ: {parsed.pj.length}
        </div>
      </div>

      <div className="space-y-4">
        <PropertyCard title="Pessoas Físicas" subtitle={`Total: ${parsed.pf.length}`} color="blue">
          {parsed.pf.length === 0 ? (
            <div className="text-gray-500">Sem registros de PF.</div>
          ) : (
            <div className="space-y-3">
              {parsed.pf.map((item, index) => (
                <EntidadePFCard key={`pf-${index}`} data={item} />
              ))}
            </div>
          )}
        </PropertyCard>

        <PropertyCard
          title="Pessoas Jurídicas"
          subtitle={`Total: ${parsed.pj.length}`}
          color="purple"
        >
          {parsed.pj.length === 0 ? (
            <div className="text-gray-500">Sem registros de PJ.</div>
          ) : (
            <div className="space-y-3">
              {parsed.pj.map((item, index) => (
                <EntidadePJCard
                  key={`pj-${index}`}
                  data={item}
                  expanded={!!expandedPjCards[index]}
                  onToggle={() =>
                    setExpandedPjCards((prev) => ({ ...prev, [index]: !prev[index] }))
                  }
                />
              ))}
            </div>
          )}
        </PropertyCard>
      </div>
    </div>
  );
};

// Componente para exibir entidade Pessoa Física
const EntidadePFCard = ({ data }) => {
  const formatCPF = (cpf) => {
    if (!cpf) return '--';
    cpf = String(cpf).replace(/\D/g, '');
    if (cpf.length !== 11) return cpf;
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  };

  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm">
      <div className="flex items-center mb-2 text-blue-700">
        <User size={16} className="mr-1.5" />
        <span className="text-sm font-semibold">{formatValue(data.nome)}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <DataItem label="CPF" value={formatCPF(data.ni)} isCopyable />
        <DataItem
          label="Nascimento"
          value={formatDateBr(data.nascimento) || formatValue(data.nascimento)}
        />
        <DataItem label="Situação" value={formatValue(data.situacao_descricao)} />
        <DataItem
          label="Data de inscrição"
          value={formatDateBr(data.data_inscricao) || formatValue(data.data_inscricao)}
        />
      </div>
    </div>
  );
};

// Componente para exibir entidade Pessoa Jurídica
const EntidadePJCard = ({ data, expanded, onToggle }) => {
  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '--';
    cnpj = String(cnpj).replace(/\D/g, '');
    if (cnpj.length !== 14) return cnpj;
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatTelefones = (telefones) => {
    if (!telefones || !Array.isArray(telefones) || !telefones.length) return '--';
    return telefones.map((tel) => `(${tel.ddd || ''}) ${tel.numero || ''}`).join(', ');
  };

  const formatEndereco = () => {
    const endereco = data.endereco;
    if (!endereco) return '--';

    const partes = [
      endereco.tipo_logradouro,
      endereco.logradouro,
      endereco.numero && `nº ${endereco.numero}`,
      endereco.complemento,
      endereco.bairro && `, ${endereco.bairro}`,
      endereco.cep && `CEP: ${endereco.cep}`,
      endereco.municipio?.descricao && `, ${endereco.municipio.descricao}`,
      endereco.uf && `-${endereco.uf}`,
      endereco.pais?.descricao && `, ${endereco.pais.descricao}`,
    ].filter(Boolean);

    return partes.join(' ');
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm">
        <div className="flex items-center mb-2 text-indigo-700">
          <Building size={16} className="mr-1.5" />
          <span className="text-sm font-semibold">{formatValue(data.nome_empresarial)}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DataItem label="CNPJ" value={formatCNPJ(data.ni)} isCopyable />
          <DataItem label="Tipo estabelecimento" value={formatValue(data.tipo_estabelecimento)} />
          <DataItem label="Nome fantasia" value={formatValue(data.nome_fantasia)} />
          <DataItem
            label="Data abertura"
            value={formatDateBr(data.data_abertura) || formatValue(data.data_abertura)}
          />
          <DataItem label="Natureza jurídica" value={formatValue(data.natureza_juridica)} />
          <div className="col-span-2 mt-2">
            <button
              onClick={onToggle}
              className="flex items-center text-blue-600 hover:text-blue-800 transition-colors font-medium text-sm"
            >
              {expanded ? (
                <>
                  <ChevronUp size={16} className="mr-1" />
                  Ocultar detalhes
                </>
              ) : (
                <>
                  <ChevronDown size={16} className="mr-1" />
                  Ver mais detalhes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {expanded && data.endereco && (
        <div className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm">
          <div className="flex items-center mb-2 text-blue-700">
            <MapPin size={16} className="mr-1.5" />
            <span className="text-sm font-semibold">Detalhes de contato e endereço</span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <DataItem label="Endereço completo" value={formatEndereco()} />

            {Array.isArray(data.telefones) && data.telefones.length > 0 && (
              <DataItem
                icon={<Phone size={16} />}
                label="Telefones"
                value={formatTelefones(data.telefones)}
              />
            )}

            {data.correio_eletronico && data.correio_eletronico !== '--' && (
              <DataItem
                icon={<Mail size={16} />}
                label="Email"
                value={formatValue(data.correio_eletronico)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntidadesTab;
